import React, { forwardRef } from 'react';

const getButtonClasses = ({ variant = 'default', size = 'default', className = '' }) => {
  // Base classes
  const baseClasses = 'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background focus-visible:outline-none focus-visible:ring ring-[#A9DFBF] focus-visible:ring-offset-0 disabled:pointer-events-none disabled:opacity-50';

  // Variant classes
  const variantClasses = {
    default: 'bg-accent text-accent-foreground hover:opacity-80',
    destructive: 'bg-destructive text-destructive-foreground',
    outline: 'border border-2 border-accent bg-background text-accent hover:bg-accent-subdued hover:text-accent-foreground',
    secondary: 'bg-screen-foreground text-secondary-foreground hover:bg-gray-100 dark:hover:bg-gray-700',
    ghost: 'hover:bg-gray-100 dark:hover:bg-gray-700',
    link: 'text-primary underline-offset-4 hover:underline',
  };

  // Size classes
  const sizeClasses = {
    default: 'px-4 py-2 text-base',
    sm: 'rounded-md px-3 py-1 text-sm',
    lg: 'rounded-md px-5 py-3 text-lg',
    icon: 'h-10 w-10',
  };

  return [
    baseClasses,
    variantClasses[variant],
    sizeClasses[size],
    className
  ].filter(Boolean).join(' ');
};

const Button = forwardRef(
  ({ 
    className, 
    variant, 
    size, 
    asChild = false, 
    children,
    ...props 
  }, ref) => {
    // If asChild is true and the first child is a valid element, clone it with our props
    if (asChild && React.isValidElement(children)) {
      return React.cloneElement(children, {
        ...props,
        className: getButtonClasses({ variant, size, className }),
        ref: ref,
      });
    }

    // Otherwise render a regular button
    return (
      <button
        className={getButtonClasses({ variant, size, className })}
        ref={ref}
        {...props}
      >
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';

export { Button }; 